import { View, StyleSheet, ActivityIndicator } from "react-native";
import { useState, useEffect } from "react";
import {
  useGetCoursesBySchoolByQueryQuery,
  useAssignCourseToFacultyMutation,
  useCreateCoursePlaceholderMutation,
  useRemoveCourseAssignmentFromFacultyMutation,
  useDeleteCourseCascadeMutation,
  useEditCourseMutation,
  useCloneCourseMutation,
  useGenerateCourseContentMutation,
  useBulkAssessmentCreateMutation,
  GetCoursesBySchoolByQueryDocument,
  Course,
  useGetUserPermissionsQuery,
} from "../../graphql/generated/graphql";
import AdminLayout from "../../layouts/AdminLayout.web";
import { MotiView } from "moti";
import { useAppConfig } from "../../AppConfigProvider";
import AdminTable from "../../components/adminDashboard/AdminTable/AdminTable";
import AdminTableHeader from "../../components/adminDashboard/AdminTable/AdminTableHeader";
import AdminTablePagination from "../../components/adminDashboard/AdminTable/AdminTablePagination";
import ManageCoursesTable, { ManageCoursesModalState } from "../../components/adminDashboard/ManageCoursesTable";
import { PlusCircle } from "../../components/svgIcons";
import usePagination from "../../hooks/usePagination";
import useSortingDropdown from "../../hooks/useSortingDropdown";
import NewCustomAxioActionModal from "../../containers/settings/UsersTabContainer/NewCustomAxioActionModal";
import AssignFacultyToCourseListContainer from "../../containers/settings/CoursesTabContainer/AssignFacultyToCourseListContainer";
import RemoveFacultyFromCourseListContainer from "../../containers/settings/CoursesTabContainer/RemoveFacultyFromCourseListContainer";
import AddCourseModal from "../../containers/settings/CoursesTabContainer/AddCourseModalContainer";
import EditCourseModalContainer from "../../containers/settings/CoursesTabContainer/EditCourseModalContainer";
import BulkUploadCourseModalContainer from "../../containers/settings/CoursesTabContainer/BulkUploadCourseModalContainer";
import BulkAssessmentModal from "../../components/adminDashboard/BulkAssessmentModal/BulkAssessmentModal";
import CustomText from "../../components/common/general/CustomText/CustomText";
import CustomSpacing from "../../components/common/layout/CustomSpacing/CustomSpacing";
import CustomButton from "../../components/common/general/CustomButton/CustomButton";
import { Upload } from "@phosphor-icons/react/dist/csr/Upload";
import { PlusCircle as Plus } from "@phosphor-icons/react";
import { RolePermissions } from "../../constants/rolesAndPermissions";

const SORT_ITEMS = [
  { label: "Date (Newest First)", value: "date_desc" },
  { label: "Date (Oldest First)", value: "date_asc" },
  { label: "Name (A-Z)", value: "name_asc" },
  { label: "Name (Z-A)", value: "name_desc" },
  { label: "Enrollment (High - Low)", value: "enrollment_desc" },
  { label: "Enrollment (Low - High)", value: "enrollment_asc" },
];

const modals = {
  removeTeacher: {
    title: "Remove Faculty from Course?",
    ariaLabel: "Remove Faculty From Course Modal",
    warningText:
      "Following faculty will no longer have access to the course. This action cannot be undone, and all associated data will be deleted.",
    actionButtonText: "Remove from Course",
  },
  deleteCourse: {
    title: "Delete Course?",
    ariaLabel: "Delete Course Modal",
    warningText:
      "Are you sure you want to permanently delete this course? This action cannot be undone, and all associated data will be deleted.",
    actionButtonText: "Delete Course",
  },
  deleteConfirmation: {
    title: "Course Deleted",
    ariaLabel: "Course Deleted Modal",
    warningText: "The course has been successfully deleted.",
    actionButtonText: "OK",
  },
  cloneCourse: {
    title: "Clone Course?",
    ariaLabel: "Clone Course Modal",
    warningText: "Are you sure you want to clone this course?",
    actionButtonText: "Clone Course",
  },
  processingCloneCourse: {
    title: "Processing Clone Course...",
    ariaLabel: "Processing Clone Course Modal",
    warningText: "Please wait while we clone the course.",
    actionButtonText: "CLOSE",
  },
  cloneCourseConfirmation: {
    title: "Course Cloned",
    ariaLabel: "Course Cloned Modal",
    warningText: "The course has been successfully cloned.",
    actionButtonText: "OK",
  },
  bulkAssessmentProcessing: {
    title: "Creating Bulk Assessments...",
    ariaLabel: "Bulk Assessment Processing Modal",
    warningText:
      "Please wait while we create final assessments for all courses. This may take a few minutes. You can close this modal and check the assessments later.",
    actionButtonText: "CLOSE",
  },
  bulkAssessmentConfirmation: {
    title: "Bulk Assessments Created",
    ariaLabel: "Bulk Assessment Confirmation Modal",
    warningText: "Final assessments have been successfully created for all eligible courses.",
    actionButtonText: "OK",
  },
};

function ManageCoursesView() {
  const appConfig = useAppConfig();
  const schoolId = appConfig.axioSchools[0];

  const [searchQuery, setSearchQuery] = useState("");
  const [modalState, setModalState] = useState({
    state: ManageCoursesModalState.CLOSED,
    courseId: "",
    courseName: "",
    isCourseProcessing: false,
  });
  const [bulkAssessmentModalVisible, setBulkAssessmentModalVisible] = useState(false);

  const { sortBy, setSortBy, sortDropdownOpen, setSortDropdownOpen } = useSortingDropdown({
    defaultSortBy: "date_desc",
  });

  const { page, setPage, pageSize, setPageSize } = usePagination({ itemsPerPage: 5 });

  const {
    data: coursesData,
    refetch: coursesRefetch,
    loading: coursesLoading,
  } = useGetCoursesBySchoolByQueryQuery({
    variables: {
      schoolId,
      searchQuery,
      page,
      pageSize,
      sortBy,
    },
    skip: !schoolId,
  });

  const { data: userPermissionsData } = useGetUserPermissionsQuery();
  const isAdmin = userPermissionsData?.getUserPermissions.includes(RolePermissions.wildcard);
  const totalCount = coursesData?.getCoursesBySchoolByQuery?.data.totalCount ?? 0;
  const totalPages = Math.ceil(totalCount / pageSize);

  // Mutations
  const [assignCourseToFaculty] = useAssignCourseToFacultyMutation();
  const [removeCourseFromFaculty] = useRemoveCourseAssignmentFromFacultyMutation();
  const [createCoursePlaceholder] = useCreateCoursePlaceholderMutation();
  const [deleteCourseCascade] = useDeleteCourseCascadeMutation();
  const [editCourse] = useEditCourseMutation();
  const [cloneCourse] = useCloneCourseMutation();
  const [generateCourseContent] = useGenerateCourseContentMutation();
  const [bulkAssessmentCreate] = useBulkAssessmentCreateMutation();

  const refetchQueries = [
    {
      query: GetCoursesBySchoolByQueryDocument,
      variables: {
        schoolId,
        searchQuery,
        page,
        pageSize,
        sortBy,
      },
    },
  ];

  useEffect(() => {
    setPage(1);
  }, [searchQuery, sortBy]);

  useEffect(() => {
    coursesRefetch();
  }, [searchQuery, page, pageSize, sortBy]);

  const handleActionModalOpen = (
    courseId: string,
    courseName: string,
    modalType: ManageCoursesModalState,
    isCourseProcessing: boolean
  ) => {
    setModalState({
      state: modalType,
      courseId,
      courseName,
      isCourseProcessing,
    });
  };

  const handleModalClose = () => {
    setModalState({ state: ManageCoursesModalState.CLOSED, courseId: "", courseName: "", isCourseProcessing: false });
  };

  const handleDeleteCourse = async () => {
    if (modalState.isCourseProcessing) {
      return;
    }

    await deleteCourseCascade({
      variables: {
        id: modalState.courseId,
      },
      refetchQueries,
    });

    setModalState({
      state: ManageCoursesModalState.DELETE_CONFIRMATION,
      courseId: "",
      courseName: "",
      isCourseProcessing: false,
    });
    setSearchQuery("");
  };

  const handleCloneCourse = async () => {
    if (modalState.isCourseProcessing) {
      return;
    }

    setModalState({
      state: ManageCoursesModalState.PROCESSING_CLONE_COURSE,
      courseId: "",
      courseName: "",
      isCourseProcessing: true,
    });

    await cloneCourse({
      variables: { courseId: modalState.courseId },
      refetchQueries,
    });

    setModalState({
      state: ManageCoursesModalState.CLONE_COURSE_CONFIRMATION,
      courseId: "",
      courseName: "",
      isCourseProcessing: false,
    });
    setSearchQuery("");
  };

  const assignFaculty = async (facultyIds: string | string[]) => {
    try {
      const userIds = Array.isArray(facultyIds) ? facultyIds : [facultyIds];

      await assignCourseToFaculty({
        variables: {
          courseId: modalState.courseId,
          userIds: userIds,
        },
        refetchQueries,
      });

      handleModalClose();
    } catch (error) {
      console.error("Error assigning faculty:", error);
    }
  };

  const removeFaculty = async (facultyIds: string | string[]) => {
    try {
      const userIds = Array.isArray(facultyIds) ? facultyIds : [facultyIds];

      await removeCourseFromFaculty({
        variables: {
          courseId: modalState.courseId,
          userIds: userIds,
        },
        refetchQueries,
      });

      handleModalClose();
    } catch (error) {
      console.error("Error removing faculty:", error);
    }
  };

  const handleEditCourse = async (courseData: {
    courseName?: string;
    courseCode?: string;
    objectives?: string;
    description?: string;
    cost?: number;
    targetAudience?: string;
    thumbnail?: string;
  }): Promise<{ success: boolean }> => {
    if (modalState.isCourseProcessing) {
      return { success: false };
    }

    try {
      const response = await editCourse({
        variables: {
          id: modalState.courseId,
          course: {
            name: courseData.courseName,
            course_code: courseData.courseCode,
            objectives: courseData.objectives?.split("\n").filter((line: string) => line.trim() !== "") || [],
            description: courseData.description,
            cost: courseData.cost,
            targetAudience: courseData.targetAudience,
            thumbnail: courseData.thumbnail,
          },
        },
        refetchQueries,
      });

      return {
        success: !!response.data?.editCourse?.success,
      };
    } catch (error) {
      console.error("Error editing course:", error);
      return { success: false };
    }
  };

  const addCourse = async (courseData: {
    courseName?: string;
    courseCode?: string;
    degrees?: string[];
    description?: string;
    objectives?: string;
    targetAudience?: string;
    cost?: number;
  }) => {
    try {
      const result = await createCoursePlaceholder({
        variables: {
          name: courseData.courseName,
          courseCode: courseData.courseCode,
          schoolId,
          description: courseData.description,
          objectives: courseData.objectives,
          targetAudience: courseData.targetAudience,
          cost: courseData.cost,
          degreeIds: courseData.degrees,
        },
        refetchQueries,
      });

      return result.data;
    } catch (error) {
      return null;
    }
  };

  const handleBulkAssessmentSubmit = async (data: {
    numberOfQuestions: number;
    timeLimit: number;
    activityState: any;
    scheduledDate?: string | Date;
  }) => {
    setBulkAssessmentModalVisible(false);

    const result = await bulkAssessmentCreate({
      variables: {
        input: {
          numberOfQuestions: data.numberOfQuestions,
          timeLimit: data.timeLimit,
          activityState: data.activityState,
          scheduledDate: data.scheduledDate,
        },
      },
    });

    if (result.data?.bulkAssessmentCreate?.success) {
      setModalState({
        state: ManageCoursesModalState.BULK_ASSESSMENT_PROCESSING,
        courseId: "",
        courseName: "",
        isCourseProcessing: true,
      });
    }
  };

  const handleBulkAssessmentClose = () => {
    setBulkAssessmentModalVisible(false);
  };

  return (
    <AdminLayout withPadding={false}>
      <View style={styles.pageBackground}>
        <View style={styles.contentContainer}>
          <MotiView
            from={{
              opacity: 0,
              translateY: 20,
              scale: 1,
            }}
            animate={{
              opacity: 1,
              translateY: 0,
              scale: 1,
            }}
            delay={10}
            // @ts-expect-error - transition is not typed
            transition={{ type: "timing", duration: 500 }}>
            <View>
              <CustomText
                text="Manage Courses"
                size="m"
                useTranslationText={false}
                textType="display"
                weight="ultraBold"
                role="heading"
                aria-level="1"
                style={{ color: "#101828" }}
              />

              <CustomSpacing type="vertical" size="s" />

              <CustomText
                text="Create, edit, and manage all courses in your LMS."
                size="m"
                weight="medium"
                useTranslationText={false}
                style={{ color: "#667085" }}
              />
              <CustomSpacing type="vertical" size="xl" />
              <View style={{ flexDirection: "row", justifyContent: "space-between", alignItems: "center" }}>
                <View>
                  {coursesData?.getCoursesBySchoolByQuery?.data?.courses?.length > 0 && (
                    <>
                      <CustomText
                        style={{ color: "#101828" }}
                        size="l"
                        weight="bold"
                        text={`Total Courses (${totalCount})`}
                        useTranslationText={false}
                      />
                    </>
                  )}
                </View>
                <View style={{ flexDirection: "row", alignItems: "center" }}>
                  <CustomButton
                    styleType="primaryLight"
                    bold={true}
                    text="Bulk Upload"
                    size="m"
                    style={{ borderColor: "#05060F1A", borderWidth: 1 }}
                    leftIcon={<Upload color={"#3E68FE"} />}
                    onPress={() => handleActionModalOpen("", "", ManageCoursesModalState.BULK_UPLOAD_COURSE, false)}
                  />
                  <CustomSpacing type="horizontal" size="m" />
                  <CustomButton
                    styleType="primary"
                    bold={true}
                    text="Add a Course"
                    size="m"
                    leftIcon={<PlusCircle color={"#FFFFFF"} />}
                    onPress={() => handleActionModalOpen("", "", ManageCoursesModalState.ADD_COURSE, false)}
                  />
                </View>
              </View>
              <CustomSpacing type="vertical" size="xl" />
            </View>
            <AdminTable>
              <AdminTableHeader
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                sortBy={sortBy}
                setSortBy={setSortBy}
                sortDropdownOpen={sortDropdownOpen}
                setSortDropdownOpen={setSortDropdownOpen}
                sortItems={SORT_ITEMS}
              />

              {coursesLoading ? (
                <View testID="courses-loader" style={styles.loaderContainer}>
                  <ActivityIndicator testID="loader" size="large" color="#0084FF" />
                </View>
              ) : (coursesData?.getCoursesBySchoolByQuery?.data?.courses?.length ?? 0) > 0 ? (
                <ManageCoursesTable
                  courses={coursesData?.getCoursesBySchoolByQuery?.data?.courses ?? []}
                  onActionModalOpen={handleActionModalOpen}
                  hasDegrees={appConfig.hasDegrees}
                />
              ) : (
                <View style={styles.emptyState}>
                  <CustomText text="No courses found." />
                </View>
              )}

              {totalCount > 0 && (
                <AdminTablePagination
                  currentPage={page}
                  totalPages={totalPages}
                  totalItems={totalCount}
                  itemsPerPage={pageSize}
                  onPageChange={setPage}
                  onItemsPerPageChange={(newPageSize) => {
                    setPageSize(newPageSize);
                    setPage(1);
                  }}
                />
              )}
            </AdminTable>
          </MotiView>

          {/* Modals */}
          <NewCustomAxioActionModal
            visible={modalState.state === ManageCoursesModalState.DELETE_COURSE}
            onClose={handleModalClose}
            onAction={handleDeleteCourse}
            modalTexts={modals.deleteCourse}
            contextualText={modalState.courseName}
          />
          <NewCustomAxioActionModal
            visible={modalState.state === ManageCoursesModalState.DELETE_CONFIRMATION}
            onClose={handleModalClose}
            modalTexts={modals.deleteConfirmation}
            contextualText={modalState.courseName}
          />
          <NewCustomAxioActionModal
            visible={modalState.state === ManageCoursesModalState.CLONE_COURSE}
            onClose={handleModalClose}
            onAction={handleCloneCourse}
            modalTexts={modals.cloneCourse}
            contextualText={modalState.courseName}
          />
          <NewCustomAxioActionModal
            visible={modalState.state === ManageCoursesModalState.PROCESSING_CLONE_COURSE}
            onClose={handleModalClose}
            modalTexts={modals.processingCloneCourse}
            contextualText={modalState.courseName}
          />
          <NewCustomAxioActionModal
            visible={modalState.state === ManageCoursesModalState.CLONE_COURSE_CONFIRMATION}
            onClose={handleModalClose}
            modalTexts={modals.cloneCourseConfirmation}
            contextualText={modalState.courseName}
          />
          <NewCustomAxioActionModal
            visible={modalState.state === ManageCoursesModalState.BULK_ASSESSMENT_PROCESSING}
            onClose={handleModalClose}
            modalTexts={modals.bulkAssessmentProcessing}
            contextualText=""
          />
          <NewCustomAxioActionModal
            visible={modalState.state === ManageCoursesModalState.BULK_ASSESSMENT_CONFIRMATION}
            onClose={handleModalClose}
            modalTexts={modals.bulkAssessmentConfirmation}
            contextualText=""
          />
          <AssignFacultyToCourseListContainer
            visible={modalState.state === ManageCoursesModalState.ASSIGN_FACULTY}
            onClose={handleModalClose}
            onAction={assignFaculty}
          />
          <RemoveFacultyFromCourseListContainer
            visible={modalState.state === ManageCoursesModalState.EDIT_FACULTY}
            onClose={handleModalClose}
            onAction={removeFaculty}
            courseId={modalState.courseId}
          />
          <AddCourseModal
            visible={modalState.state === ManageCoursesModalState.ADD_COURSE}
            onClose={handleModalClose}
            onSubmit={addCourse}
          />
          <EditCourseModalContainer
            visible={modalState.state === ManageCoursesModalState.EDIT_COURSE}
            onClose={handleModalClose}
            onSubmit={handleEditCourse}
            courseData={
              coursesData?.getCoursesBySchoolByQuery?.data?.courses?.find(
                (course) => course?.id === modalState.courseId
              ) as Course
            }
          />
          {modalState.state === ManageCoursesModalState.BULK_UPLOAD_COURSE && (
            <BulkUploadCourseModalContainer
              visible={true}
              onClose={handleModalClose}
              onSubmit={async (formData) => {
                return await addCourse(formData);
              }}
              onGenerateCourse={async (courseId, numberOfModules, numberOfActivities) => {
                await generateCourseContent({
                  variables: {
                    courseId,
                    numberOfModules,
                    numberOfActivities,
                  },
                });
              }}
            />
          )}
          <BulkAssessmentModal
            visible={bulkAssessmentModalVisible}
            onClose={handleBulkAssessmentClose}
            onSubmit={handleBulkAssessmentSubmit}
            isProcessing={false}
          />
        </View>
      </View>
    </AdminLayout>
  );
}

const styles = StyleSheet.create({
  loaderContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: 16,
  },
  emptyState: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: 16,
  },
  pageBackground: {
    flex: 1,
    backgroundColor: "#F2F4F7",
    //@ts-expect-error - works on web
    minHeight: "100vh",
  },
  contentContainer: {
    padding: 24,
    flex: 1,
  },
});

export default ManageCoursesView;
